import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import importacaoService from '../services/importacaoService';
import toast from 'react-hot-toast';
import { FiPlus, FiDollarSign, FiPercent } from 'react-icons/fi';
import ImportacaoForm from '../components/ImportacaoForm';
import ImportacaoList from '../components/ImportacaoList';
import StatisticsCard from '../components/StatisticsCard';
import DesembaracoModal from '../components/DesembaracoModal';

export default function VisaoGeralPage() {
  const navigate = useNavigate();
  const { usuario } = useAuthStore();

  const [importacoes, setImportacoes] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [importacaoSelecionada, setImportacaoSelecionada] = useState(null);
  const [importacaoParaEditar, setImportacaoParaEditar] = useState(null);

  const carregarDados = useCallback(async () => {
    try {
      setIsLoading(true);
      const [importacoesData, statsData] = await Promise.all([
        importacaoService.listar(),
        importacaoService.obterEstatisticas(),
      ]);
      setImportacoes(importacoesData.content || []);
      setStatistics(statsData);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!usuario) {
      navigate('/login');
      return;
    }
    carregarDados();
  }, [usuario, navigate, carregarDados]);

  const handleFormSucesso = () => {
    setShowForm(false);
    setImportacaoParaEditar(null);
    carregarDados();
  };

  const handleEditClick = (importacao) => {
    setImportacaoParaEditar(importacao);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleOpenDesembaraco = (importacao) => {
    setImportacaoSelecionada(importacao);
    setIsModalOpen(true);
  };

  const handleDesembaracoRegistrado = () => {
    setIsModalOpen(false);
    setImportacaoSelecionada(null);
    carregarDados();
  };

  return (
    <div className="animate-in fade-in duration-300">
      {statistics && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            <StatisticsCard title="Compras Finalizadas" value={statistics.comprasFinalizadas} />
            <StatisticsCard title="Compras a Finalizar" value={statistics.comprasAFinalizar} />
            <StatisticsCard title="Total Pago (30%)" value={`R$ ${Number(statistics.totalPago30 || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`} />
            <StatisticsCard title="Restante a Pagar (70%)" value={`R$ ${Number(statistics.totalProjecao70 || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`} />
            <StatisticsCard title="Desembaraço (50%)" value={`R$ ${Number(statistics.totalProjecao50 || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`} />
            <StatisticsCard title="Custo Total das Importações" value={`R$ ${Number(statistics.totalProjecaoGeral || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            <div className="bg-white rounded-lg p-5 shadow-sm border border-slate-200 border-t-4 border-t-indigo-600">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 flex items-center gap-1.5 mb-2">
                <FiDollarSign size={12} className="text-indigo-600" /> Volume Total Financiado (Finimp)
              </p>
              <p className="text-3xl font-bold text-slate-900 font-mono">
                R$ {Number(statistics.totalFinimp || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
              </p>
            </div>

            <div className="bg-white rounded-lg p-5 shadow-sm border border-slate-200 border-t-4 border-t-amber-500">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 flex items-center gap-1.5 mb-2">
                <FiPercent size={12} className="text-amber-500" /> Custo Total de Juros (Finimp)
              </p>
              <p className="text-3xl font-bold text-slate-900 font-mono">
                R$ {Number(statistics.totalJurosFinimp || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
              </p>
            </div>
          </div>
        </>
      )}

      {importacoes && importacoes.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm p-5 mb-8 border border-slate-200">
          <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-4">Feed de Últimas Importações</h3>
          <div className="flex flex-wrap gap-4">
            {importacoes.slice(0, 4).map(imp => (
              <div key={imp.id} className="flex-1 min-w-[150px] sm:min-w-[200px] border border-slate-200 rounded-md p-4 flex flex-col justify-between">
                <div>
                  <span className="text-xs text-gray-400 font-bold uppercase">
                    {new Date(imp.dataImportacao).toLocaleDateString('pt-BR', { timeZone: 'UTC' })}
                  </span>
                  <p className="text-sm font-bold text-gray-900 mt-1">{imp.produto}</p>
                </div>
                <div className="mt-4">
                  {imp.status === 'DESEMBARACADO' ? (
                    <span className="bg-slate-100 text-emerald-700 border border-slate-200 px-2.5 py-1 rounded-md text-xs font-semibold">Desembaraçado</span>
                  ) : (
                    <span className="bg-slate-100 text-amber-700 border border-slate-200 px-2.5 py-1 rounded-md text-xs font-semibold">Pendente</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mb-8">
        <button
          onClick={() => {
            setShowForm(!showForm);
            if (showForm) setImportacaoParaEditar(null);
          }}
          className={`flex items-center gap-2 px-5 py-2.5 rounded-md font-semibold shadow-sm transition-all ${
            showForm ? 'bg-slate-100 text-slate-700 hover:bg-slate-200' : 'bg-slate-900 text-white hover:bg-slate-800'
          }`}
        >
          <FiPlus /> {showForm ? 'Cancelar' : 'Nova Importação'}
        </button>
      </div>

      {showForm && (
        <div className="bg-white rounded-lg shadow-sm p-6 mb-8 border border-slate-200 border-t-4 border-t-indigo-600">
          <ImportacaoForm
            onImportacaoCriada={handleFormSucesso}
            importacaoParaEditar={importacaoParaEditar}
          />
        </div>
      )}

      {isLoading ? (
        <div className="text-center py-12 text-gray-400">Carregando dados...</div>
      ) : (
        <ImportacaoList
          importacoes={importacoes}
          onRefresh={carregarDados}
          onEmbarcarClick={handleOpenDesembaraco}
          onEditClick={handleEditClick}
        />
      )}

      <DesembaracoModal
        isOpen={isModalOpen}
        importacao={importacaoSelecionada}
        onClose={() => setIsModalOpen(false)}
        onDesembaracoRegistrado={handleDesembaracoRegistrado}
      />
    </div>
  );
}
